/**
 * 알림 시드 — 사람마다 쌓인 앱 안 알림. 눌렀을 때 갈 화면은 `link` 에 들어 있다.
 *
 * 종류를 보고 화면을 짜 맞추지 않는다 — 서버가 넣어 준 링크로 간다.
 */
import type { Notification } from "@/types";
import { dailyEditRequests } from "./dailyExtras";
import { at } from "./seed";

/** 최다민의 수정 요청 — notif-6 이 가리킨다(날짜는 시드가 정한다). */
const choiEdit = dailyEditRequests.find((r) => r.requester_id === "u-st4");

/** 지금으로부터 몇 분 전 — 패널의 오늘·어제·이번 주 묶음이 언제 열어도 채워지게 시각을 지금에 맞춘다. */
const ago = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const HOUR = 60;
const DAY = 24 * HOUR;

/** 결재 요청 알림이 가리키는 신청 — mock 안에서만 쓴다. 핸들러가 이걸로 `pending` 을 센다. */
export type NotificationSubject =
  | { type: "vacation" | "vacation_cancel" | "schedule"; ids: string[] }
  | { type: "daily_edit"; ids: string[] };

export interface MockNotification extends Omit<Notification, "pending"> {
  user_id: string;
  subject?: NotificationSubject;
}

export const notifications: MockNotification[] = [
  {
    id: "notif-1",
    user_id: "u-staff",
    actor: { id: "u-pm", name: "노은진", avatar_url: null },
    kind: "daily_comment",
    title: "Daily 코멘트: 노은진",
    body: "실시설계 상세도 쪽 시간이 좀 많아 보여요. 어떤 작업이었는지 한 줄 더 적어 주세요.",
    link: "/daily",
    read_at: null,
    created_at: at("2026-10-21", "19:12"),
  },
  {
    id: "notif-2",
    user_id: "u-staff",
    actor: null,
    kind: "vacation_decided",
    title: "휴가 자동 승인",
    body: "2026-10-16 16:00–18:00 · 0.25일",
    link: "/attendance",
    read_at: at("2026-10-07", "10:30"),
    created_at: at("2026-10-07", "10:12"),
  },
  {
    id: "notif-3",
    user_id: "변경민",
    actor: { id: "u-st3", name: "오세진", avatar_url: null },
    kind: "vacation_requested",
    subject: { type: "vacation", ids: ["vacation-13"] },
    title: "휴가 신청: 오세진",
    body: "2026-10-23 · 1일 · 개인 연차",
    link: "/admin/attendance",
    read_at: null,
    created_at: at("2026-10-19", "09:40"),
  },

  // 김태호(관리자) — 결재 요청이 쌓이는 쪽
  {
    id: "notif-5",
    user_id: "u-owner",
    actor: { id: "u-st2", name: "김서연", avatar_url: null },
    kind: "vacation_requested",
    subject: { type: "vacation", ids: ["vacation-25", "vacation-26"] },
    title: "휴가 신청: 김서연",
    body: "2026-11-10 ~ 11-12 · 3일 · 경조사 외 1건",
    link: "/attendance",
    read_at: null,
    created_at: ago(10),
  },
  {
    id: "notif-6",
    user_id: "u-owner",
    actor: { id: "u-st4", name: "최다민", avatar_url: null },
    kind: "daily_edit_requested",
    subject: { type: "daily_edit", ids: choiEdit ? [choiEdit.id] : [] },
    title: "Daily 수정 요청: 최다민",
    body: `${choiEdit?.work_date ?? ""} 기록 · 프로젝트 시간을 고쳐 달라고 했어요`,
    // 그 사람 · 그날의 Daily 로 간다 — 거기서 고친 내용을 보고 「수정 반영」한다.
    link: choiEdit ? `/daily?edit_request=${choiEdit.id}` : "/daily",
    read_at: null,
    created_at: ago(40),
  },
  {
    id: "notif-7",
    user_id: "u-owner",
    actor: { id: "u-acc", name: "정유리", avatar_url: null },
    kind: "vacation_cancel_requested",
    subject: { type: "vacation_cancel", ids: ["vacation-20"] },
    title: "휴가 취소 신청: 정유리",
    body: "2026-10-08 · 1일 · 개인 연차",
    link: "/attendance",
    read_at: null,
    created_at: ago(3 * HOUR),
  },
  {
    id: "notif-11",
    user_id: "u-owner",
    actor: { id: "변경민", name: "변경민", avatar_url: null },
    kind: "vacation_decided",
    title: "휴가 승인: 김태호",
    body: "2026-10-10 · 1일 · 개인 연차",
    link: "/attendance",
    read_at: ago(9 * DAY),
    created_at: ago(10 * DAY),
  },
  // 이도윤(직원) — 내 신청의 결과
  {
    id: "notif-13",
    user_id: "u-staff",
    actor: { id: "u-owner", name: "김태호", avatar_url: null },
    kind: "daily_edit_decided",
    title: "Daily 수정 요청이 승인됐어요",
    body: "2026-10-20 기록",
    link: "/daily",
    read_at: ago(DAY),
    created_at: ago(DAY + 4 * HOUR),
  },
  {
    id: "notif-14",
    user_id: "u-staff",
    actor: null,
    kind: "daily_missing",
    title: "Daily를 아직 안 썼어요",
    body: "2026-10-24(금)",
    link: "/daily",
    read_at: null,
    created_at: ago(3 * DAY),
  },
];
